import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { CourseAPI } from 'service';

const LearningTimeline = ({

  timelineEvents = [
    {
      id: 1,
      type: 'chapter_start',
      title: 'Started Chapter 1',
      description: 'Introduction to AI',
      date: '2024-01-10',
      time: '09:30 AM',
      status: 'completed'
    },
    {
      id: 2,
      type: 'presentation_complete',
      title: 'Completed Presentation',
      description: 'Introduction to AI - All slides viewed',
      date: '2024-01-10',
      time: '10:15 AM',
      status: 'completed'
    },
    {
      id: 3,
      type: 'quiz_attempt',
      title: 'First Quiz Attempt',
      description: 'Score: 75% - Passed',
      date: '2024-01-10',
      time: '10:30 AM',
      status: 'completed'
    },
    {
      id: 4,
      type: 'chapter_complete',
      title: 'Chapter 1 Completed',
      description: 'Introduction to AI - Chapter unlocked Chapter 2',
      date: '2024-01-10',
      time: '10:35 AM',
      status: 'completed'
    },
    {
      id: 5,
      type: 'chapter_start',
      title: 'Started Chapter 2',
      description: 'Machine Learning Basics',
      date: '2024-01-12',
      time: '02:00 PM',
      status: 'completed'
    },
    {
      id: 6,
      type: 'presentation_progress',
      title: 'Presentation in Progress',
      description: 'Machine Learning Basics - 8 of 15 slides',
      date: '2024-01-15',
      time: '11:20 AM',
      status: 'current'
    }
  ]
}) => {


  const [timeline, setTimelineEvents] = useState([]);

  useEffect(() => {
    const fetchTimeline = async () => {
      const response = await CourseAPI.getTimeline();
      if (response.success) {
        setTimelineEvents(response.data.data.timeline);
      } else {
        console.error("Failed to fetch timeline:", response.error);
      }
    };

    fetchTimeline();
  }, []);

  const getEventIcon = (type) => {
    switch (type) {
      case 'started_chapter':
        return 'Play';
      case 'presentation_complete':
        return 'BookOpen';
      case 'presentation_progress':
        return 'BookOpen';
      case 'quiz_attempt':
        return 'Brain';
      case 'chapter_complete':
        return 'Trophy';
      default:
        return 'Circle';
    }
  };

  const getEventColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'current':
        return 'secondary';
      case 'upcoming':
        return 'text-muted';
      default:
        return 'text-secondary';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const isToday = (dateString) => {
    const today = new Date();
    const eventDate = new Date(dateString);
    return today.toDateString() === eventDate.toDateString();
  };

  const isRecent = (dateString) => {
    const today = new Date();
    const eventDate = new Date(dateString);
    const diffTime = Math.abs(today - eventDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  return (
    <div className="p-6 border bg-surface rounded-xl border-border card-shadow">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold font-heading text-text-primary">
            Learning Timeline
          </h3>
          <p className="mt-1 text-text-secondary">
            Track your learning journey and milestones
          </p>
        </div>
        <div className="flex items-center px-3 py-2 space-x-2 rounded-lg bg-secondary-50">
          <Icon name="Calendar" size={16} color="var(--color-secondary)" strokeWidth={2} />
          <span className="text-sm font-medium text-secondary-700">
            {timeline.length} Events
          </span>
        </div>
      </div>

      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border-light" />

        {/* Timeline Events */}
        <div className="space-y-6">
          {timeline.map((event, index) => (
            <div key={event.id} className="relative flex items-start space-x-4">
              {/* Timeline Dot */}
              <div className={`
                relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-4 border-surface
                ${event.type === 'completed' ? 'bg-success-50' :
                  event.type === 'started_chapter' ? 'bg-secondary-50' : 'bg-surface-secondary'}
              `}>
                <Icon
                  name={getEventIcon(event.type)}
                  size={18}
                  color={`var(--color-${getEventColor(event.status)})`}
                  strokeWidth={2}
                />
              </div>

              {/* Event Content */}
              <div className="flex-1 min-w-0 pb-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-1 space-x-2">
                      <h4 className="text-base font-semibold font-heading text-text-primary">
                        {event.title}
                      </h4>
                      {event.status === 'current' && (
                        <div className="flex items-center px-2 py-1 space-x-1 rounded-full bg-secondary-100">
                          <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
                          <span className="text-xs font-medium text-secondary-700">Active</span>
                        </div>
                      )}
                      {isToday(event.date) && (
                        <div className="px-2 py-1 rounded-full bg-accent-100">
                          <span className="text-xs font-medium text-accent-700">Today</span>
                        </div>
                      )}
                      {isRecent(event.date) && !isToday(event.date) && (
                        <div className="px-2 py-1 rounded-full bg-primary-100">
                          <span className="text-xs font-medium text-primary-700">Recent</span>
                        </div>
                      )}
                    </div>
                    <p className="mb-2 text-sm text-text-secondary">
                      {event.courseTitle}
                    </p>
                    <div className="flex items-center space-x-4 text-xs text-text-muted">
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={12} color="currentColor" strokeWidth={2} />
                        <span>{formatDate(event.date)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Clock" size={12} color="currentColor" strokeWidth={2} />
                        <span>{formatTime(event.date)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Future Events Placeholder */}
        <div className="relative flex items-start space-x-4 opacity-50">
          <div className="relative z-10 flex items-center justify-center w-12 h-12 border-4 rounded-full border-surface bg-surface-secondary">
            <Icon name="MoreHorizontal" size={18} color="var(--color-text-muted)" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0 pb-6">
            <h4 className="mb-1 text-base font-medium font-heading text-text-muted">
              More milestones coming...
            </h4>
            <p className="text-sm text-text-muted">
              Continue learning to unlock new achievements
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningTimeline;